/* eslint-disable @typescript-eslint/no-floating-promises */
import type Store from '@ember-data/store';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import sortBy from '@nullvoxpopuli/ember-composable-helpers/helpers/sort-by';
import { task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import sub_ from 'ember-math-helpers/helpers/sub';
import eq from 'ember-truth-helpers/helpers/eq';
import or from 'ember-truth-helpers/helpers/or';
import Message from 'parog-web/components/chat/message-feed/message';
import type Patient from 'parog-web/models/patient';
import type PatientChatMessageSentJournalEvent from 'parog-web/models/patient-chat-message-sent-journal-event';
import Button from 'ui-kit/components/button';
import NoContentMessage from 'ui-kit/components/no-content-message';
import fetchChatMessageJes from './utils/fetch-chat-messages';

interface Args {
  patient: Patient;
  scrollAndFocus: () => void;
}

export default class ChatMessageFeed extends Component<Args> {
  @service declare store: Store;

  @tracked private currentLoadedPage = 1;
  @tracked private totalPageCount?: number;

  get hasMoreMessageToLoad(): boolean {
    return (
      Boolean(this.totalPageCount) &&
      this.currentLoadedPage <= this.totalPageCount!
    );
  }

  get messages(): PatientChatMessageSentJournalEvent[] {
    return this.args.patient.patientChatMessageSentJournalEvents.slice();
  }

  #pageSize = 50;

  constructor(owner: Owner, args: Args) {
    super(owner, args);

    this.currentLoadedPage =
      Math.floor(
        this.args.patient.patientChatMessageSentJournalEvents.length /
          this.#pageSize
      ) || 1;
    this.fetchChatMessageJesAndScrollTask.perform();
  }

  fetchChatMessageJesAndScrollTask = task(async () => {
    await this.#fetchChatMessages();
    this.args.scrollAndFocus();
  });

  fetchChatMessageJesTask = task(async () => this.#fetchChatMessages());

  async #fetchChatMessages() {
    const res = await fetchChatMessageJes(
      this.store,
      this.args.patient,
      this.currentLoadedPage,
      this.#pageSize
    );

    this.currentLoadedPage++;
    this.totalPageCount = res.pageCount;
  }

  <template>
    <div class='message-feed'>
      {{#if
        (or
          this.fetchChatMessageJesTask.isRunning
          this.fetchChatMessageJesAndScrollTask.isRunning
        )
      }}
        <div
          class='ui small active loader inline [&.ui.loader&]:w-full [&.ui.loader]:mx-0 [&.ui.loader]:my-[1rem]'
        ></div>
      {{else if this.hasMoreMessageToLoad}}
        <div class='my-8 flex justify-center'>
          <Button
            type='button'
            class='ui basic primary mini button'
            data-test-load-more-messages
            {{on 'click' (perform this.fetchChatMessageJesTask)}}
          >
            Charger plus de messages
          </Button>
        </div>
      {{/if}}

      {{#each (sortBy 'occurredAt:asc' this.messages) as |message index|}}
        <Message
          @messageEvent={{message}}
          @isLastMessage={{eq index (sub_ this.messages.length 1)}}
        />
      {{else}}
        {{#unless this.fetchChatMessageJesTask.isRunning}}
          <NoContentMessage @alignment='left'>
            <span data-test-no-message class='message'>
              Aucun message n'a encore été envoyé ou reçu.
            </span>
          </NoContentMessage>
        {{/unless}}
      {{/each}}
    </div>
  </template>
}
